/* eslint-disable no-loop-func */
import React, { PureComponent, Fragment } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { removeIdTokenLocalStorage, ITokenMetadata } from '@sbgsports/vault-sso-auth';
import { useLocation } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
} from '@mui/material';
import { Colors, Styling, Routes } from '../../constants';

import Jobs from './VaultJobs/component';
import ExchangeJobsButton from './ExchangeJobs/component';
import UserSelect from '../ImpersonationSelect/component';
import { CatapultLogo, Separator } from '../Shared';
import { UiSection } from '../../enums';
import { getTokenMetadata } from '../../utils/sso';
import ProductMenu from './ProductMenu/component';
import UserMenu from './UserMenu/component';
import { MainNav } from './Nav';
import { IAppState } from '../../state/reducers';
import { Product, closeExchangeTemplateNotificationAction } from '../../state/product';
import { IProps, IStateProps, IDispatchProps } from './interfaces';
import ToolsMenu from './Nav/ToolsMenu/component';
import HelpMenu from './HelpMenu/component';
import { KeyRoleUserAccessPermission, selectIsUserVaultSuperAdmin } from '../../state/user/userOrgRole';
import ImpersonateUserDialog from '../ImpersonateUserDialog/component';
import { clearImpersonationStash, endImpersonationForLogout } from '../../utils/impersonation';
import { HeaderButton } from './styles';
import SettingsMenu from './SettingsMenu/component';
import { toggleContactInfoDialog } from '../../state/exchange/contactInfoDialog';
import Notifications from './Notifications/component';

const MainContainer = styled.div`
  height: ${Styling.HEADER_HEIGHT}px;
  width: 100%;
  background-color: ${Colors.BLACK_100};
  padding: ${Styling.DEFAULT_SPACING}px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`;

const InnerMainContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const BrandingContainer = styled.div`
  display: flex;
  align-items: center;
  height: 22px;
  margin-right: ${Styling.DEFAULT_SPACING_DOUBLED * 2}px;
`;

const Filler = styled.div`
  display: flex;
  flex-grow: 1;
`;

class Header extends PureComponent<IProps, { impersonateOpen: boolean }> {
  private static readonly CatapultLogoID = `${UiSection.Header}.CatapultLogo`;

  private readonly tokenMetadata: ITokenMetadata;

  constructor(props: any) {
    super(props);

    this.tokenMetadata = getTokenMetadata();
    this.state = { impersonateOpen: false };
  }

  // Logging out of an impersonated session ends that session too: the end event goes
  // first while the impersonation token is still active, then both it and the stashed
  // admin token are dropped, so nothing impersonation-related outlives the logout.
  logout = async () => {
    await endImpersonationForLogout();
    removeIdTokenLocalStorage();
    clearImpersonationStash();
    window.location.replace(process.env.REACT_APP_SSO_LOGOUT_URL);
  };

  render() {
    const {
      location,
      userHasTeam,
      userOrgRoleState,
      isExchange,
      isExchangeTemplateNotificationOpen,
      isVaultSuperAdmin,
      closeExchangeTemplateNotification,
      toggleContactInfo,
    } = this.props;
    const { impersonateOpen } = this.state;

    const { loggedIn = false, loginAs = null, useAs = null } = userOrgRoleState || {};

    if (!loggedIn || !loginAs) {
      return null;
    }

    const { userInfo } = loginAs;

    const role = useAs || loginAs;

    const hasPublishProfilePermission = role?.userAccessPermission?.publishProfile ?? false;
    const hasManageLiveClippingPermission = role?.userAccessPermission?.addLiveVideo ?? false;
    const hasManageUserOrgPermission = role?.userAccessPermission?.manageUsersOrg ?? false;
    const hasContactInfoPermission = role?.userAccessPermission?.contactInfo ?? false;

    const allowExchangeToolsForContact = isExchange && hasContactInfoPermission;

    const canSeeToolsButton = hasPublishProfilePermission || hasManageLiveClippingPermission || hasManageUserOrgPermission
      || allowExchangeToolsForContact;

    const onHome = location.pathname === Routes.HOME;
    const onExchange = location.pathname === Routes.EXCHANGE;
    const onClipping = location.pathname === Routes.VIRTUAL_CLIPPER;
    const onLiveEvents = location.pathname === Routes.MANAGE_LIVE_EVENTS;

    const hasJobReviewPermission = role?.userAccessPermission?.manageVideoClip ?? false;

    const seeJobsButton = hasJobReviewPermission && !onExchange && ((onHome || onClipping || onLiveEvents) && !isExchange);

    return (
      <MainContainer>
        <Notifications />
        <InnerMainContainer>
          <BrandingContainer>
            <CatapultLogo id={Header.CatapultLogoID} />
            <ProductMenu />
          </BrandingContainer>

          <MainNav />

          <Filler />

          {seeJobsButton && (
            <>
              <Jobs />
              <Separator horizontal={false} color={Colors.WHITE_30} />
            </>
          )}

          {isExchange && (
            <>
              <ExchangeJobsButton />
              <Separator horizontal={false} color={Colors.WHITE_30} />
            </>
          )}

          {canSeeToolsButton && (
            <>
              <ToolsMenu />
              {<Separator horizontal={false} color={Colors.WHITE_30} />}
            </>
          )}

          {/* {hasUseAsPermission && React.createElement(UserSelect)} */}
          {/* {hasUseAsPermission && <Separator horizontal={false} color={Colors.WHITE_30} />} */}

          {/* Exchange only: impersonation exists to reproduce what an exchange user sees,
              and switching product ends the session (see Header/ProductMenu). */}
          {isVaultSuperAdmin && isExchange && (
            <>
              <HeaderButton
                type="button"
                onClick={() => this.setState({ impersonateOpen: true })}
                data-tip="Impersonate a user"
                data-auto="ImpersonateUserBtn"
              >
                Impersonate
              </HeaderButton>
              <Separator horizontal={false} color={Colors.WHITE_30} />
              <ImpersonateUserDialog
                isOpen={impersonateOpen}
                onClose={() => this.setState({ impersonateOpen: false })}
              />
            </>
          )}

          <UserMenu
            userInfo={userInfo}
            tokenMetadata={this.tokenMetadata}
            logoutClicked={this.logout}
            isContactInfo={hasContactInfoPermission && userHasTeam}
            toggleContactInfo={toggleContactInfo}
          />

          <>
            <Separator horizontal={false} color={Colors.WHITE_30} />
            {isExchange && (
              <SettingsMenu />
            )}
            <HelpMenu />
          </>

          {isExchangeTemplateNotificationOpen && (
            <Dialog
              open={isExchangeTemplateNotificationOpen}
              onClose={() => closeExchangeTemplateNotification(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogTitle id="alert-dialog-title">Your Default Naming Template Has Been Changed</DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Select Edit to modify your template settings
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => closeExchangeTemplateNotification(true)} color="primary">
                  Edit
                </Button>
                <Button onClick={() => closeExchangeTemplateNotification(false)} color="primary">
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          )}

        </InnerMainContainer>
      </MainContainer>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  userHasTeam: !!state.exchange?.initExchange?.currentTeam,
  userOrgRoleState: state.user?.userOrgRole,
  isExchange: state.product?.target === Product.Exchange,
  isExchangeTemplateNotificationOpen: state.product?.isExchangeTemplateNotificationOpen ?? false,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
});

const mapDispatchToProps: IDispatchProps = {
  closeExchangeTemplateNotification: closeExchangeTemplateNotificationAction,
  toggleContactInfo: toggleContactInfoDialog,
};

const ConnectedHeader = connect(
  mapStateToProps,
  mapDispatchToProps,
)(Header);

// Wrapper component to provide location via hook
const HeaderWithRouter: React.FC = () => {
  const location = useLocation();
  return <ConnectedHeader location={location} />;
};

export default HeaderWithRouter;
